/**
 * OmegaProposalsTile — Phase 8 dashboard tile.
 *
 * Shows pending OMEGA Brain proposals generated by the three-layer
 * cognitive loop (Model → Prediction → Self-modification).
 *
 * Constitutional invariant: proposals are never self-executed.
 * This tile only renders pending proposals and exposes sign/dismiss
 * affordances — the POST calls still require a valid admin JWT; any
 * non-admin user sees 403 and the tile stays unchanged.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '../../../../api/http';
import { omegaQueryKeys, useOmegaProposals } from '../hooks/useOmegaState';
import type { OmegaProposal } from '../data/types';

const MAX_VISIBLE = 3;

export function OmegaProposalsTile() {
  const { data, isPending, isError } = useOmegaProposals();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: omegaQueryKeys.proposals() });

  const sign = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/admin/proposal/${id}/sign`, { method: 'POST' }),
    onSettled: invalidate,
  });

  const dismiss = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/admin/proposal/${id}/dismiss`, { method: 'POST', body: {} }),
    onSettled: invalidate,
  });

  const pending = (data?.proposals ?? []).filter((p) => p.status === 'pending');
  const overflow = Math.max(0, pending.length - MAX_VISIBLE);
  const visible = pending.slice(0, MAX_VISIBLE);

  return (
    <section
      aria-labelledby="proposals-heading"
      style={{
        background: 'var(--omega-surface)',
        border: '1px solid var(--omega-rim)',
        padding: 'var(--omega-gap-3)',
        marginTop: 'var(--omega-gap-3)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--omega-gap-2)',
          marginBottom: 'var(--omega-gap-2)',
        }}
      >
        <h2
          id="proposals-heading"
          style={{
            margin: 0,
            fontSize: '11pt',
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--omega-ink)',
          }}
        >
          OMEGA Proposals
        </h2>
        {(data?.pending ?? 0) > 0 && (
          <span
            style={{
              fontSize: '9pt',
              padding: '1px 7px',
              border: '1px solid var(--omega-state-degraded)',
              color: 'var(--omega-state-degraded)',
              letterSpacing: '0.06em',
              fontWeight: 600,
            }}
          >
            {data!.pending} pending
          </span>
        )}
        <span style={{ flex: 1 }} />
        {data && (
          <span
            style={{
              fontSize: '9pt',
              color: 'var(--omega-ink-subtle)',
              letterSpacing: '0.04em',
            }}
          >
            {data.total} total
          </span>
        )}
      </div>

      {/* Body */}
      {isPending && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '10pt' }}>
          Loading…
        </div>
      )}

      {isError && (
        <div style={{ color: 'var(--omega-state-refused)', fontSize: '10pt' }}>
          Failed to load proposals.
        </div>
      )}

      {!isPending && !isError && visible.length === 0 && (
        <div
          style={{
            color: 'var(--omega-ink-subtle)',
            fontSize: '10pt',
            letterSpacing: '0.04em',
            fontStyle: 'italic',
          }}
        >
          No pending proposals — the brain is watching.
        </div>
      )}

      {visible.map((p) => (
        <ProposalRow
          key={p.id}
          proposal={p}
          onSign={() => sign.mutate(p.id)}
          onDismiss={() => dismiss.mutate(p.id)}
          busy={sign.isPending || dismiss.isPending}
        />
      ))}

      {overflow > 0 && (
        <div
          style={{
            marginTop: 'var(--omega-gap-1)',
            fontSize: '9pt',
            color: 'var(--omega-ink-subtle)',
            letterSpacing: '0.06em',
          }}
        >
          + {overflow} more pending
        </div>
      )}
    </section>
  );
}

function ProposalRow({
  proposal: p,
  onSign,
  onDismiss,
  busy,
}: {
  proposal: OmegaProposal;
  onSign: () => void;
  onDismiss: () => void;
  busy: boolean;
}) {
  const age = relativeAge(p.proposed_at_iso);
  const confColor =
    p.confidence === 'high'
      ? 'var(--omega-state-refused)'
      : p.confidence === 'medium'
        ? 'var(--omega-state-degraded)'
        : 'var(--omega-ink-subtle)';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--omega-gap-2)',
        padding: 'var(--omega-gap-1) 0',
        borderTop: '1px solid var(--omega-rim)',
      }}
    >
      {/* Confidence badge */}
      <span
        style={{
          flexShrink: 0,
          fontSize: '8pt',
          padding: '1px 5px',
          border: `1px solid ${confColor}`,
          color: confColor,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          fontWeight: 600,
          marginTop: 2,
        }}
      >
        {p.confidence}
      </span>

      {/* Action + justification */}
      <div className="ltr-content" style={{ flex: 1, overflow: 'hidden' }}>
        <div
          className="omega-mono"
          style={{
            fontSize: '10pt',
            color: 'var(--omega-ink)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {p.action_kind}
        </div>
        <div
          style={{
            fontSize: '9pt',
            color: 'var(--omega-ink-muted)',
            marginTop: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {p.justification}
        </div>
      </div>

      {/* Age */}
      <span
        style={{
          flexShrink: 0,
          fontSize: '9pt',
          color: 'var(--omega-ink-subtle)',
          whiteSpace: 'nowrap',
          marginTop: 2,
        }}
      >
        {age}
      </span>

      {/* Actions */}
      <div style={{ flexShrink: 0, display: 'flex', gap: 6 }}>
        <ActionBtn label="Sign" onClick={onSign} disabled={busy} accent />
        <ActionBtn label="Dismiss" onClick={onDismiss} disabled={busy} />
      </div>
    </div>
  );
}

function ActionBtn({
  label,
  onClick,
  disabled,
  accent,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  accent?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        fontSize: '8pt',
        padding: '2px 8px',
        border: `1px solid ${accent ? 'var(--omega-state-healthy)' : 'var(--omega-rim-strong)'}`,
        background: 'transparent',
        color: accent
          ? 'var(--omega-state-healthy)'
          : 'var(--omega-ink-muted)',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        fontFamily: 'inherit',
      }}
    >
      {label}
    </button>
  );
}

function relativeAge(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const ms = Date.now() - then;
  if (ms < 60_000) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
